export type RuntimeConfig = {
  envName: string;
  apiUrl: string;
  marketDataApiUrl: string;
  marketDataApiKey: string;
  marketDataSearchNormalizedPath: string;
  marketDataReferenceDataPath: string;
  keycloakUrl: string;
  keycloakRealm: string;
  keycloakClientId: string;
  syncfusionLicenseKey: string;
  agGridKey: string;
};

let cached: RuntimeConfig | null = null;
/** True after loadRuntimeConfig finishes (remote or vite fallback). */
let resolved = false;

/** Auto-falls back to Vite env so tests and early imports stay green before loadRuntimeConfig. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) cached = buildConfigFromViteEnv();
  return cached;
}

function marketDataProxyUrl(apiUrl: string): string {
  return `${apiUrl.replace(/\/$/, '')}/api/market-data`;
}

export function buildConfigFromViteEnv(): RuntimeConfig {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:9001';
  return {
    envName: import.meta.env.VITE_ENV_NAME || 'Development',
    apiUrl,
    marketDataApiUrl: import.meta.env.VITE_MARKETDATA_API_URL || marketDataProxyUrl(apiUrl),
    marketDataApiKey: '',
    marketDataSearchNormalizedPath: import.meta.env.VITE_MARKETDATA_SEARCH_NORMALIZED_PATH || '/search/symbols/normalized',
    marketDataReferenceDataPath: import.meta.env.VITE_MARKETDATA_REFERENCE_DATA_PATH || '/reference-data',
    keycloakUrl: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:9090',
    keycloakRealm: import.meta.env.VITE_KEYCLOAK_REALM || 'ledgerrealm',
    keycloakClientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'ledgervue-web',
    syncfusionLicenseKey: import.meta.env.VITE_SYNCFUSION_LICENSE_KEY || '',
    agGridKey: import.meta.env.VITE_AGGRID_KEY || '',
  };
}

/** Load /config.json when present (Azure); otherwise fall back to Vite env (local/dev/vitest). */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (resolved && cached) return cached;
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok) {
      const json = await res.json();
      // Map camelCase from server; tolerate missing fields by merging vite defaults
      const defaults = buildConfigFromViteEnv();
      const apiUrl = json.apiUrl ?? defaults.apiUrl;
      cached = {
        envName: json.envName ?? defaults.envName,
        apiUrl,
        marketDataApiUrl: json.marketDataApiUrl ?? marketDataProxyUrl(apiUrl),
        // Never accept a Market Data key from /config.json — the API proxy uses the user JWT.
        marketDataApiKey: '',
        marketDataSearchNormalizedPath: json.marketDataSearchNormalizedPath ?? defaults.marketDataSearchNormalizedPath,
        marketDataReferenceDataPath: json.marketDataReferenceDataPath ?? defaults.marketDataReferenceDataPath,
        keycloakUrl: json.keycloakUrl ?? defaults.keycloakUrl,
        keycloakRealm: json.keycloakRealm ?? defaults.keycloakRealm,
        keycloakClientId: json.keycloakClientId ?? defaults.keycloakClientId,
        syncfusionLicenseKey: json.syncfusionLicenseKey ?? defaults.syncfusionLicenseKey,
        agGridKey: json.agGridKey ?? defaults.agGridKey,
      };
      resolved = true;
      return cached;
    }
  } catch {
    // local vite / tests
  }
  cached = buildConfigFromViteEnv();
  resolved = true;
  return cached;
}

/** Test helper */
export function resetRuntimeConfigForTests(config?: RuntimeConfig | null): void {
  if (config === undefined) {
    cached = null;
    resolved = false;
    return;
  }
  cached = config;
  resolved = config !== null;
}
